import React, { useState } from 'react';
import { Star, AlertCircle, Check } from 'lucide-react';
import reviewService, { ReviewRequest } from '../../services/reviewService';
import StarRating from './StarRating';

interface ReviewFormProps {
  revieweeId: string;
  revieweeName: string;
  revieweeRole: 'ADVISOR' | 'ANALYST';
  existingReview?: any; // For editing
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const ReviewForm: React.FC<ReviewFormProps> = ({
  revieweeId,
  revieweeName,
  revieweeRole,
  existingReview,
  onSuccess,
  onCancel
}) => {
  const [formData, setFormData] = useState<ReviewRequest>({
    revieweeId,
    overallRating: existingReview?.overallRating || 0,
    title: existingReview?.title || '',
    reviewText: existingReview?.reviewText || '',
    communicationRating: existingReview?.communicationRating || 0,
    expertiseRating: existingReview?.expertiseRating || 0,
    responsivenessRating: existingReview?.responsivenessRating || 0,
    valueRating: existingReview?.valueRating || 0,
    researchQualityRating: existingReview?.researchQualityRating || 0,
    accuracyRating: existingReview?.accuracyRating || 0,
    actionableInsightsRating: existingReview?.actionableInsightsRating || 0,
    serviceType: existingReview?.serviceType || '',
    engagementDuration: existingReview?.engagementDuration || '',
    wouldRecommend: existingReview?.wouldRecommend ?? true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isValid = formData.overallRating > 0 && formData.reviewText.length >= 10;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValid) {
      setError('Please provide an overall rating and at least 10 characters of review text.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submitData = { ...formData };
      const relevantKeys = new Set(ratingCategories.map((c) => c.key));
      ['communicationRating','expertiseRating','responsivenessRating','valueRating','researchQualityRating','accuracyRating','actionableInsightsRating'].forEach((k) => {
        if (!relevantKeys.has(k)) delete (submitData as any)[k];
      });

      if (existingReview) {
        await reviewService.updateReview(existingReview.id, submitData);
      } else {
        await reviewService.createReview(submitData);
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const ratingCategories = revieweeRole === 'ADVISOR'
    ? [
        { key: 'communicationRating', label: 'Communication' },
        { key: 'expertiseRating', label: 'Expertise & Knowledge' },
        { key: 'responsivenessRating', label: 'Responsiveness' },
        { key: 'valueRating', label: 'Value for Money' }
      ]
    : [
        { key: 'researchQualityRating', label: 'Research Quality' },
        { key: 'accuracyRating', label: 'Accuracy' },
        { key: 'actionableInsightsRating', label: 'Actionable Insights' },
        { key: 'responsivenessRating', label: 'Responsiveness' }
      ];

  if (success) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="bg-green-100 rounded-full p-3">
            <Check className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <h3 className="text-lg font-semibold text-green-900 mb-2">
          Review Submitted Successfully!
        </h3>
        <p className="text-green-700">
          Thank you for sharing your feedback. Your review is pending approval.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Overall Rating */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Overall Rating <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-4">
          <StarRating
            rating={formData.overallRating}
            size="lg"
            interactive
            onChange={(rating) => setFormData({ ...formData, overallRating: rating })}
          />
          {formData.overallRating > 0 && (
            <span className="text-sm text-gray-600">
              {['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][formData.overallRating - 1]}
            </span>
          )}
        </div>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Review Title (Optional)
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Summarize your experience"
          maxLength={200}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Review Text */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Your Review <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.reviewText}
          onChange={(e) => setFormData({ ...formData, reviewText: e.target.value })}
          placeholder={`Share your experience working with ${revieweeName}...`}
          rows={6}
          minLength={10}
          maxLength={5000}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
        <div className="mt-1 text-sm text-gray-500 flex justify-between">
          <span>{formData.reviewText.length >= 10 ? '✓' : ''} Minimum 10 characters</span>
          <span>{formData.reviewText.length} / 5000</span>
        </div>
      </div>

      {/* Category Ratings */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Category Ratings (Optional)
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ratingCategories.map((category) => (
            <div key={category.key} className="flex items-center justify-between">
              <span className="text-sm text-gray-700">{category.label}</span>
              <StarRating
                rating={formData[category.key as keyof ReviewRequest] as number || 0}
                size="sm"
                interactive
                onChange={(rating) =>
                  setFormData({ ...formData, [category.key]: rating })
                }
              />
            </div>
          ))}
        </div>
      </div>

      {/* Additional Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Service Type (Optional)
          </label>
          <input
            type="text"
            value={formData.serviceType}
            onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
            placeholder="e.g., Financial Planning, Investment Advice"
            maxLength={100}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Engagement Duration (Optional)
          </label>
          <select
            value={formData.engagementDuration}
            onChange={(e) => setFormData({ ...formData, engagementDuration: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select duration</option>
            <option value="Less than 3 months">Less than 3 months</option>
            <option value="3-6 months">3-6 months</option>
            <option value="6-12 months">6-12 months</option>
            <option value="1-2 years">1-2 years</option>
            <option value="2+ years">2+ years</option>
          </select>
        </div>
      </div>

      {/* Would Recommend */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="wouldRecommend"
          checked={formData.wouldRecommend}
          onChange={(e) => setFormData({ ...formData, wouldRecommend: e.target.checked })}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="wouldRecommend" className="text-sm text-gray-700">
          I would recommend {revieweeName} to others
        </label>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Buttons */}
      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={loading || !isValid}
          className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Submitting...' : existingReview ? 'Update Review' : 'Submit Review'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Guidelines */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <p className="font-medium text-gray-700 mb-2">Review Guidelines:</p>
        <ul className="space-y-1 list-disc list-inside">
          <li>Be honest and specific about your experience</li>
          <li>Focus on professional services received</li>
          <li>Avoid personal attacks or inappropriate language</li>
          <li>Your review will be checked before being published</li>
        </ul>
      </div>
    </form>
  );
};

export default ReviewForm;
